import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import gettext from '@eb/gettext';
import { TYPE_ERROR } from '@eb/eds-notification';
import { EmptyState } from '@eb/eds-empty-state';
import { AlertChunky } from '@eb/eds-iconography';
import { ProgressIndicator } from '@eb/eds-progress-indicator';
import { CalendarGraphic } from '@eb/eds-iconography';
import { ENV_SHAPE_PROP_TYPE } from '@eb/site-structure';

import {
    DEFAULT_SEARCH_QUERY,
    DEFAULT_PAGE_NUMBER,
    DEFAULT_PAGE_SIZE,
    DEFAULT_ORGANIZER_ID,
    PUBLISHABLE_EVENT_SHAPE,
    STATUS_FILTER_PROP_TYPE,
    ORGANIZER_SHAPE,
} from '../constants';
import EventsList from './EventsList';
import EventsScreenLayout from './EventsScreenLayout';

export default class EventsScreen extends PureComponent {
    static propTypes = {
        baseUrl: PropTypes.string.isRequired,
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        organizationId: PropTypes.string.isRequired,
        status: STATUS_FILTER_PROP_TYPE.isRequired,
        onLoadEvents: PropTypes.func.isRequired,
        onFilterEvents: PropTypes.func.isRequired,
        addMainNotification: PropTypes.func,
        events: PropTypes.arrayOf(PUBLISHABLE_EVENT_SHAPE),
        organizers: PropTypes.arrayOf(ORGANIZER_SHAPE),
        searchQuery: PropTypes.string,
        currentPage: PropTypes.number,
        pageSize: PropTypes.number,
        pageCount: PropTypes.number,
        organizerId: PropTypes.string,
        crumb: PropTypes.string,
        user: PropTypes.object,
    };

    static defaultProps = {
        searchQuery: DEFAULT_SEARCH_QUERY,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_PAGE_SIZE,
        pageCount: 1,
        organizerId: DEFAULT_ORGANIZER_ID,
    };

    componentDidMount() {
        const { events, organizerId } = this.props;

        // NOTE: Undefined `events` signifies that it hasn't
        // been loaded yet, which would be different from empty
        // which signifies that they have been loaded but there
        // aren't any.
        if (!events || organizerId !== DEFAULT_ORGANIZER_ID) {
            this._loadEvents();
        }
    }

    componentDidUpdate(prevProps) {
        const { events } = this.props;
        const { events: prevEvents } = prevProps;

        if (!isEqual(prevEvents, events)) {
            this._loadEvents();
        }
    }

    _showFailureNotification(message) {
        const { addMainNotification } = this.props;

        if (addMainNotification) {
            addMainNotification({
                children: message,
                type: TYPE_ERROR,
                shouldScrollTo: true,
                iconType: <AlertChunky />,
                hasCloseButton: true,
            });
        }
    }

    _loadEvents = () => {
        const {
            onLoadEvents,
            organizationId,
            status,
            searchQuery,
            pageNumber,
            pageSize,
            organizerId,
        } = this.props;

        onLoadEvents(organizationId, {
            status,
            searchQuery,
            pageNumber,
            pageSize,
            organizerId,
        }).catch(() =>
            this._showFailureNotification(
                gettext(
                    'There was an error accessing this page. Please check the url address and try again.',
                ),
            ),
        );
    };

    _handlePageChange = (pageNumber) => {
        const {
            onFilterEvents,
            status,
            pageSize,
            organizerId,
            searchQuery,
        } = this.props;

        // NOTE: Effectively what will happen is that `onFilterEvents` will do a client-side
        // redirect to the events page w/ the updated filterParams. This will trigger
        // componentDidUpdate above, which will cause new events matching the params to load.
        // This makes the route the source of truth
        onFilterEvents({
            status,
            pageSize,
            pageNumber,
            searchQuery,
            organizerId,
        });
    };

    _renderContent = () => {
        const {
            env,
            events,
            status,
            pageNumber,
            pageCount,
            crumb,
            isExternalContentLoading,
            user,
        } = this.props;

        if (!events || isExternalContentLoading) {
            return (
                <div data-spec="events-screen-loading">
                    <ProgressIndicator shape="linear" style="gradient" />
                </div>
            );
        }

        if (!isEmpty(events)) {
            return (
                <EventsList
                    serverUrl={env.serverUrl}
                    events={events}
                    currencyFormat={env.currencyFormat}
                    statusFilter={status}
                    pageNumber={pageNumber}
                    pageCount={pageCount}
                    onPageChange={this._handlePageChange}
                    crumb={crumb}
                    user={user}
                />
            );
        }

        return (
            <div
                className="eds-l-mar-top-10"
                data-spec="events-screen-empty-state"
            >
                <EmptyState
                    graphicType={<CalendarGraphic />}
                    description={gettext('No events to show')}
                />
            </div>
        );
    };

    render() {
        return (
            <EventsScreenLayout {...this.props}>
                {this._renderContent()}
            </EventsScreenLayout>
        );
    }
}
